import clsx from 'clsx';
import { TrendingUp, Users, MousePointerClick, Target, DollarSign } from 'lucide-react';
import { AppButton } from '@/shared/components/ui/AppButton/AppButton';
import { AppCard } from '@/shared/components/ui/AppCard/AppCard';
import styles from './BudgetPacingBreakdown.module.scss';

interface PacingInfo {
  currentPhase: string;
  budgetMultiplier: number;
  explanation: string;
  daysUntilEvent: number;
}

interface BudgetPacingBreakdownProps {
  dailyBudgetCents: number;
  totalBudgetCents: number;
  estimatedReach?: number;
  estimatedClicks?: number;
  estimatedConversions?: number;
  estimatedCostPerConversionCents?: number;
  confidence?: string;
  pacing?: PacingInfo;
  onApply: () => void;
}

function formatCents(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

function formatNumber(num: number): string {
  if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(1)}M`;
  if (num >= 1_000) return `${(num / 1_000).toFixed(1)}K`;
  return num.toLocaleString();
}

export function BudgetPacingBreakdown({
  dailyBudgetCents,
  totalBudgetCents,
  estimatedReach,
  estimatedClicks,
  estimatedConversions,
  estimatedCostPerConversionCents,
  confidence,
  pacing,
  onApply,
}: BudgetPacingBreakdownProps) {
  return (
    <AppCard className={styles.breakdown}>
      <div className={styles.header}>
        <TrendingUp size={18} />
        <span className={styles.headerTitle}>AI Budget Recommendation</span>
        {confidence && (
          <span
            className={clsx(styles.confidenceBadge, styles[`confidence${capitalize(confidence)}`])}
          >
            {confidence.toUpperCase()}
          </span>
        )}
      </div>

      <div className={styles.budgetSummary}>
        <div className={styles.budgetAmount}>
          <DollarSign size={20} />
          <span className={styles.budgetValue}>{formatCents(dailyBudgetCents)}</span>
          <span className={styles.budgetPeriod}>/day</span>
        </div>
        <span className={styles.budgetTotal}>{formatCents(totalBudgetCents)} total</span>
      </div>

      {pacing && (
        <div className={styles.pacingSection}>
          <span className={styles.pacingTitle}>Pacing Strategy</span>
          <div className={styles.pacingBar}>
            <div
              className={clsx(styles.pacingSegment, styles.phaseAwareness)}
              style={{ flex: pacing.currentPhase === 'awareness' ? 1 : 0.3 }}
            >
              <span className={styles.pacingLabel}>Awareness</span>
            </div>
            <div
              className={clsx(styles.pacingSegment, styles.phaseConsideration)}
              style={{ flex: pacing.currentPhase === 'consideration' ? 1 : 0.3 }}
            >
              <span className={styles.pacingLabel}>Consideration</span>
            </div>
            <div
              className={clsx(styles.pacingSegment, styles.phaseUrgency)}
              style={{ flex: pacing.currentPhase === 'urgency' ? 1 : 0.3 }}
            >
              <span className={styles.pacingLabel}>Urgency</span>
            </div>
          </div>
          <p className={styles.pacingExplanation}>{pacing.explanation}</p>
          <span className={styles.pacingMeta}>
            {pacing.daysUntilEvent} days until event &middot; {pacing.budgetMultiplier}x multiplier
          </span>
        </div>
      )}

      <div className={styles.metricsGrid}>
        {estimatedReach != null && (
          <div className={styles.metric}>
            <Users size={16} />
            <span className={styles.metricValue}>{formatNumber(estimatedReach)}</span>
            <span className={styles.metricLabel}>Est. Reach</span>
          </div>
        )}
        {estimatedClicks != null && (
          <div className={styles.metric}>
            <MousePointerClick size={16} />
            <span className={styles.metricValue}>{formatNumber(estimatedClicks)}</span>
            <span className={styles.metricLabel}>Est. Clicks</span>
          </div>
        )}
        {estimatedConversions != null && (
          <div className={styles.metric}>
            <Target size={16} />
            <span className={styles.metricValue}>{formatNumber(estimatedConversions)}</span>
            <span className={styles.metricLabel}>Est. Conversions</span>
          </div>
        )}
        {estimatedCostPerConversionCents != null && (
          <div className={styles.metric}>
            <DollarSign size={16} />
            <span className={styles.metricValue}>
              {formatCents(estimatedCostPerConversionCents)}
            </span>
            <span className={styles.metricLabel}>Est. CPA</span>
          </div>
        )}
      </div>

      <AppButton type="button" variant="secondary" onClick={onApply} className={styles.applyButton}>
        Apply Recommendation
      </AppButton>
    </AppCard>
  );
}

function capitalize(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}
